'use client';

import { cn } from '@/lib/utils';
import { ChatMessageItem } from './chat-message';
import { useChatScroll } from './hooks/use-chat-scroll';
import {
    type ChatMessage,
    useRealtimeChat,
} from './hooks/use-realtime-chat';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button/variants';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useUserContext } from '@/contexts/user';
import {
    CHAT_FILE_MAX_SIZE,
    type ChatFile,
    formatFileSize,
    isRenderableImage,
    removeChatFile,
    toChatFile,
    uploadChatFile,
} from '@/lib/chat/chatFiles';
import { FileText, Loader2, Paperclip, Send, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase/client';

interface RealtimeChatProps {
    roomName: string
    username: string
    userId: string
    onMessage?: (messages: ChatMessage[]) => void
    messages?: ChatMessage[]
}

/**
 * Realtime chat component
 * @param roomName - The name of the room to join. Each room is a unique chat.
 * @param username - The username of the user
 * @param onMessage - The callback function to handle the messages. Useful if you want to store the messages in a database.
 * @param messages - The messages to display in the chat. Useful if you want to display messages from a database.
 * @returns The chat component
 */
export const RealtimeChat = ({
    roomName,
    username,
    userId,
    onMessage,
}: RealtimeChatProps) => {
    const { permissions } = useUserContext();
    const { toast } = useToast();
    const canSendNotifications = permissions.has('notifications.send');
    const canSendFiles = permissions.has('chat.files.send');
    const [sendNotification, setSendNotification] = useState(false);

    const { containerRef, scrollAreaRef, handleScroll, scrollToBottom, scrollToBottomIfNearBottom } = useChatScroll();

    const {
        messages: realtimeMessages,
        sendMessage,
        deleteMessage,
        deletedMessageIds,
        isConnected,
    } = useRealtimeChat({
        roomName,
        username,
        userId,
    });

    const [initialMessages, setInitialMessage] = useState<ChatMessage[]>([]);

    const [newMessage, setNewMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [messageToDelete, setMessageToDelete] = useState<ChatMessage | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        (async () => {
            const { data, error } = await supabase
                .from("chat_rooms_messages_view")
                .select("id, sent_at, content, user_id, user_name, with_notification, file_path, file_name, file_type, file_size")
                .eq("topic", roomName);
            if (error) {
                console.error(error);
                return;
            }

            setInitialMessage(data.map(row => ({
                content: row.content,
                id: row.id,
                sentAt: row.sent_at,
                userId: row.user_id,
                userName: row.user_name,
                withNotification: row.with_notification,
                file: toChatFile(row.file_path, row.file_name, row.file_type, row.file_size),
            })));
        })();
    }, [roomName]);

    // Merge realtime messages with initial messages
    const allMessages = useMemo(() => {
        const mergedMessages = [...initialMessages, ...realtimeMessages];
        // Remove duplicates based on message id
        const uniqueMessages = mergedMessages.filter(
            (message, index, self) => index === self.findIndex((m) => m.id === message.id)
        );
        // Remove messages that have been deleted
        const remainingMessages = uniqueMessages.filter((message) => !deletedMessageIds.has(message.id));
        // Sort by creation date
        const sortedMessages = remainingMessages.sort((a, b) => a.sentAt?.localeCompare(b.sentAt) ?? 0);

        return sortedMessages;
    }, [initialMessages, realtimeMessages, deletedMessageIds]);

    useEffect(() => {
        if (onMessage) {
            onMessage(allMessages);
        }
    }, [allMessages, onMessage]);

    useEffect(() => {
        // Scroll to bottom whenever messages change
        scrollToBottom();
    }, [allMessages, scrollToBottom]);

    // Auto-grow the message box to fit its content; CSS max-h caps it and it scrolls beyond that.
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        // scrollHeight excludes the border, which box-sizing: border-box needs included
        const borderHeight = textarea.offsetHeight - textarea.clientHeight;
        textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;
        // Only show a scrollbar once the content outgrows the max-h cap
        textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? 'auto' : 'hidden';
    }, [newMessage]);

    // Local preview of a picked image, released when it is replaced or sent.
    useEffect(() => {
        if (!selectedFile || !isRenderableImage({ type: selectedFile.type })) {
            setSelectedFilePreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(selectedFile);
        setSelectedFilePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [selectedFile]);

    const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        // Reset so picking the same file again still fires onChange.
        e.target.value = '';
        if (!file) return;

        if (file.size > CHAT_FILE_MAX_SIZE) {
            toast({
                title: 'Filen är för stor',
                description: `Filer får vara högst ${formatFileSize(CHAT_FILE_MAX_SIZE)}.`,
                variant: 'destructive',
            });
            return;
        }
        setSelectedFile(file);
    }, [toast]);

    const canSend = isConnected && !sending && (newMessage.trim().length > 0 || selectedFile !== null);

    const handleSendMessage = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            const content = newMessage.trim();
            if ((!content && !selectedFile) || !isConnected || sending) return;

            setSending(true);
            let uploaded: ChatFile | null = null;
            try {
                if (selectedFile) {
                    uploaded = await uploadChatFile(userId, selectedFile);
                }
                const { error } = await sendMessage(content, sendNotification, uploaded);
                if (error) throw error;

                setNewMessage('');
                setSelectedFile(null);
            } catch (error) {
                console.error(error);
                // The message never made it, so don't leave the upload behind.
                if (uploaded) removeChatFile(uploaded.path);
                toast({
                    title: 'Meddelandet kunde inte skickas',
                    description: 'Försök igen.',
                    variant: 'destructive',
                });
            } finally {
                setSending(false);
            }
        },
        [newMessage, selectedFile, isConnected, sending, userId, sendMessage, sendNotification, toast]
    );

    // // Enter sends and Shift+Enter inserts a new line. Touch devices have no
    // // practical Shift, so there Enter always inserts a new line.
    // const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    //     if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    //     if (window.matchMedia('(pointer: coarse)').matches) return;
    //     e.preventDefault();
    //     e.currentTarget.form?.requestSubmit();
    // }, []);

    const handleConfirmDelete =useCallback(async () => {
        if (!messageToDelete) return;
        setDeleting(true);
        await deleteMessage(messageToDelete);
        setDeleting(false);
        setMessageToDelete(null);
    }, [messageToDelete, deleteMessage]);

    return (
        <div className="flex flex-col h-full w-full bg-background text-foreground antialiased">
            {/* Messages */}
            <div
                ref={scrollAreaRef}
                onScroll={handleScroll}
                className="flex-1 min-h-0 overflow-y-auto py-4 pr-4 pl-6 space-y-4 scrollbar-stable scrollbar scrollbar-track-transparent scrollbar-thumb-primary"
            >
                {allMessages.length === 0 ? (
                    <div className="text-center text-sm text-muted-foreground">
                        No messages yet. Start the conversation!
                    </div>
                ) : null}
                <div className="space-y-1">
                    {allMessages.map((message, index) => {
                        const prevMessage = index > 0 ? allMessages[index - 1] : null;
                        const showHeader = !prevMessage || prevMessage.userId !== message.userId;

                        return (
                            <div
                                key={message.id}
                                className="animate-in fade-in slide-in-from-bottom-4 duration-300"
                            >
                                <ChatMessageItem
                                    message={message}
                                    isOwnMessage={message.userId === userId}
                                    showHeader={showHeader}
                                    onDelete={() => setMessageToDelete(message)}
                                    onImageLoad={scrollToBottomIfNearBottom}
                                />
                            </div>
                        );
                    })}
                </div>
                <div ref={containerRef} />
            </div>

            <form onSubmit={handleSendMessage} className="flex flex-col w-full gap-2 p-4">
                {selectedFile && (
                    <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-2 w-fit max-w-full animate-in fade-in duration-200">
                        {selectedFilePreviewUrl
                            ? <img src={selectedFilePreviewUrl} alt={selectedFile.name} className="size-12 shrink-0 rounded object-cover" />
                            : <FileText className="size-6 shrink-0 text-muted-foreground" />}
                        <div className="flex flex-col min-w-0 text-sm">
                            <span className="truncate">{selectedFile.name}</span>
                            <span className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSelectedFile(null)}
                            disabled={sending}
                            aria-label="Ta bort bifogad fil"
                            className="text-muted-foreground hover:text-foreground shrink-0"
                        >
                            <X className="size-4" />
                        </button>
                    </div>
                )}
                {canSendNotifications && (
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="send-notification"
                            className="block"
                            checked={sendNotification}
                            onCheckedChange={v => setSendNotification(Boolean(v))}
                        />
                        <Label htmlFor="send-notification" className="text-xs text-muted-foreground font-normal cursor-pointer">
                            Skicka en notis till alla användare i den här gruppen
                        </Label>
                    </div>
                )}
                <div className="flex items-end gap-2">
                    {canSendFiles && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleFileSelected}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="rounded-full shrink-0"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={!isConnected || sending}
                                aria-label="Bifoga fil"
                            >
                                <Paperclip className="size-5" />
                            </Button>
                        </>
                    )}
                    <Textarea
                        ref={textareaRef}
                        rows={1}
                        className={cn(
                            // Grows with its content (see the effect above) up to max-h, then scrolls
                            'flex-1 min-w-0 min-h-0 max-h-40 resize-none rounded-2xl bg-background text-base md:text-base scrollbar-stable scrollbar scrollbar-track-transparent scrollbar-thumb-primary'
                        )}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        // onKeyDown={handleKeyDown}
                        placeholder="Skriv ett meddelande..."
                        disabled={!isConnected}
                    />
                    {(canSend || sending) && (
                        <Button
                            className="aspect-square rounded-full shrink-0 animate-in fade-in slide-in-from-right-4 duration-300"
                            type="submit"
                            disabled={!canSend}
                            aria-label="Skicka"
                        >
                            {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                        </Button>
                    )}
                </div>
            </form>

            <AlertDialog open={messageToDelete !== null} onOpenChange={open => { if (!open) setMessageToDelete(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Ta bort meddelande?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Detta tar bort ditt meddelande permanent. Det går inte att ångra.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction
                            className={cn(buttonVariants({ variant: 'destructive' }))}
                            onClick={handleConfirmDelete}
                            disabled={deleting}
                        >
                            Ta bort
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
